import Column from '@components/Column';
import Image from '@components/Image';
import Indicator from '@components/Indicator';
import Text from '@components/Text';
import colors from '@constants/colors';
import { GiftItemType } from '@models/gift';
import { PrepaidCardImage, PrepaidCardImageRow } from '@models/prepaidCard';
import { useFlow } from '@router/stackflow';
import { useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';

import styles from './styles.module.scss';

interface Props {
    data: PrepaidCardImageRow;
    type: 'GIFT' | 'BUY';
    giftItemId?: string;
}

const GRID_COUNT = 6;

function CardList({ data, type, giftItemId }:Props) {
  const { categoryName, images } = data;
  const [indicator, setIndicator] = useState<number>(0);
  const [sliderIndex, setSliderIndex] = useState<number>(0);
  const settings = {
    infinite: true,
    slidesToScroll: 1,
    slidesToShow: 1,
    arrows: false,
    dots: false,
    speed: 400,
    swipe: true,
    swipeToSlide: true,
    easing: 'ease-out',
    variableWidth: true,
    centerMode: true,
    centerPadding: '30px',
    afterChange: setSliderIndex,
  };
  const sliderRef = useRef<Slider>(null);
  const isGift = type === 'GIFT';

  const cardPage = useMemo(() => {
    const result: PrepaidCardImage[][] = [[]];
    for (let i = 0; i < images.length; i += 1) {
      if (result[result.length - 1].length === GRID_COUNT) {
        result.push([]);
      }
      result[result.length - 1].push(images[i]);
    }
    return result;
  }, [images]);
  const { push } = useFlow();
  const onCardClick = (id: string) => {
    if (isGift) {
      push('GiftOrderActivity', {
        giftItemId: giftItemId!,
        type: GiftItemType.CASH,
        qty: 1,
        giftCardImageId: id,
      });
    } else {
      push('PrepaidCardImageActivity', { prepaidCardImageId: id });
    }
  };

  return (
        <Column align='center'
                className={styles.container}
                spacing={20}>
            {images.length > 1 ? (
                <Slider {...settings}
                        ref={sliderRef} >
                    {images.map((card, i) => (
                            <div key={card.id}
                                 onClick={() => onCardClick(card.id)}
                                 className={`${styles.card} ${i === sliderIndex && styles.active}`}>
                                <Image src={card.imageUrl}
                                       width={256}
                                       height={168}
                                       alt={card.title}/>
                            </div>
                    ))}
                </Slider>
            ) : (
                <div onClick={() => onCardClick(images[0].id)}
                     className={`${styles.card} ${styles.active}`}>
                    <Image src={images[0].imageUrl}
                           width={256}
                           height={168}
                           alt={images[0].title}/>
                </div>
            )}
            <Text size='xl' fontWeight='bold' color={colors.gray7}>
                {categoryName}
            </Text>
            <div className={styles.gridContainer}>
                <div className={styles.gridContents}
                     onScroll={(e) => {
                       const { scrollLeft, offsetWidth } = e.currentTarget;
                       setIndicator(Math.round(scrollLeft / offsetWidth));
                     }}>
                    {cardPage.map((page, i) => (
                        <div className={styles.grid}
                             key={i}>
                            {page.map((card, j) => (
                                <Image src={card.imageUrl}
                                       className={styles.cardSm}
                                       key={`small ${i} ${j}`}
                                       width={72}
                                       height={48}
                                       alt={i.toString()}
                                       onClick={() => sliderRef.current?.slickGoTo(i * GRID_COUNT + j)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
                {cardPage.length >= 2 && (
                    <div className={styles.indicator}>
                        <Indicator total={cardPage.length} current={indicator} />
                    </div>
                )}
            </div>
        </Column>
  );
}

export default CardList;
